import { encryptString } from 'utils/Helpers';
import { Order } from 'types/OrderTypes';
import { UserType } from 'types/CustomerTypes';
import { Constants } from 'utils/Constants';
import { TransactionOrderLineType } from 'types/HistoryTypes';
import { configureOpenApiClients } from '../../api/configure';
import {
  ticketAddConsent,
  ticketCancelPaymentTransaction,
  ticketCapture,
  ticketGetPaymentTransactionStatus,
  ticketPay as ticketPayRequest,
  ticketSendPdf,
  ticketSendReceipt,
} from '../../api/v1';
import { ticketRefundTicket, ticketReserve, ticketReviewOrder, ticketSendSms } from '../../api/v2';

export const useTicketApi = (token: string, user: UserType, order: Order) => {
  configureOpenApiClients();

  const sendPdf = async () =>
    await ticketSendPdf({
      path: { orderId: order.orderID },
      headers: { authorization: 'Bearer ' + token },
      body: {
        firstName: order.contactInformation.firstName,
        lastName: order.contactInformation.surname,
        landCode: '+47',
        number: order.contactInformation.telephoneNumberNoCountryCode,
        email: order.contactInformation.email,
        pdfLanguage: 'nob',
      },
    });

  const cancelPaymentTransaction = async ({ gaPaymentId, gaTransactionId }) =>
    await ticketCancelPaymentTransaction({
      path: { paymentId: gaPaymentId, transactionId: gaTransactionId },
      headers: { authorization: 'Bearer ' + token },
    });

  const getVippsTransactionStatus = async ({ gaPaymentId, gaTransactionId }) =>
    await ticketGetPaymentTransactionStatus({
      path: { paymentId: gaPaymentId, transactionId: gaTransactionId },
      headers: { authorization: 'Bearer ' + token },
    });

  const captureTicket = async ({ gaPaymentId, gaTransactionId }) =>
    await ticketCapture({
      path: { paymentId: Number(gaPaymentId), transactionId: Number(gaTransactionId) },
      headers: { authorization: 'Bearer ' + token },
    });

  const ticketPay = async () => {
    const query = {
      paymentType: String(order.paymentMethod),
      callbackUrl: undefined,
      recurringPaymentId: undefined,
    };
    const headers = { authorization: 'Bearer ' + token, phoneNumber: undefined };

    if (order.recurringCard) {
      query.paymentType = order.recurringCard.paymentType;
      query.callbackUrl = `${window.location.protocol}//${window.location.host}${Constants.ROUTE_NETS_REDIRECT}`;
      query.recurringPaymentId = order.recurringCard.recurringPaymentId;
    } else {
      if (order.paymentMethod === Constants.VIPPS) {
        const encryptedToken = encryptString(token);
        const url = `${window.location.protocol}//${window.location.host}${
          Constants.ROUTE_VIPPS_REDIRECT
        }?distribution=${order.deliveryMethod}&sendSms=${
          order.contactInformation.sendSMS ? 'true' : 'false'
        }&token=${encryptedToken}`;

        query.callbackUrl = url;
        headers.phoneNumber = order.contactInformation.telephoneNumberNoCountryCode;
      } else {
        query.callbackUrl = `${window.location.protocol}//${window.location.host}${Constants.ROUTE_NETS_REDIRECT}`;
      }
    }
    return await ticketPayRequest({ path: { orderId: order.orderID }, headers, query });
  };

  const sendReceipt = async (historyOrderID: string) => {
    return ticketSendReceipt({
      path: { orderId: historyOrderID },
      headers: { authorization: 'Bearer ' + token },
      body: {
        email: user.email,
        language: 'nob',
      },
    });
  };

  const sendSMS = async () => {
    return ticketSendSms({
      path: { orderId: order.orderID },
      headers: { authorization: 'Bearer ' + token },
      body: {
        countryCode: order.contactInformation.countryCode.split('+')[1],
        number: order.contactInformation.telephoneNumberNoCountryCode,
      },
    });
  };

  const reserve = async (offers) => {
    return await ticketReserve({
      headers: { authorization: 'Bearer ' + token },
      body: { offers },
    });
  };

  const reviewOrder = async () =>
    await ticketReviewOrder({
      path: { orderId: order.orderID },
      headers: { authorization: 'Bearer ' + token },
    });

  const addConsent = async () =>
    await ticketAddConsent({
      path: { orderId: order.orderID },
      headers: { authorization: 'Bearer ' + token },
    });

  const refundTicket = async (orderLine: TransactionOrderLineType) => {
    await ticketRefundTicket({
      path: { orderId: orderLine.orderId },
      headers: { authorization: 'Bearer ' + token },
      query: { orderLines: orderLine.refundOptions.orderLines },
    });
  };

  return {
    sendPdf,
    cancelPaymentTransaction,
    getVippsTransactionStatus,
    captureTicket,
    ticketPay,
    sendReceipt,
    sendSMS,
    reserve,
    reviewOrder,
    addConsent,
    refundTicket,
  };
};
